/**
 * DlgThresholdSettings - Manager-only DLG configuration UI
 * 
 * Allows managers to edit shop-level DLG thresholds, 
 * and toggle dlg_enabled / pensieve_dlg_learning_enabled flags.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Shield, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

interface ThresholdValues {
  focused_elevated_minutes: number;
  focused_saturated_minutes: number;
  balanced_elevated_minutes: number;
  balanced_saturated_minutes: number;
  occasional_elevated_minutes: number;
  occasional_saturated_minutes: number;
  consecutive_elevated: number;
  consecutive_saturated: number;
  active_sessions_elevated: number;
  active_sessions_saturated: number;
  pause_fatigue_threshold: number;
  rolling_window_hours: number;
  rolling_elevated_minutes: number;
  rolling_saturated_minutes: number;
}

const DEFAULTS: ThresholdValues = {
  focused_elevated_minutes: 300,
  focused_saturated_minutes: 420,
  balanced_elevated_minutes: 180,
  balanced_saturated_minutes: 300,
  occasional_elevated_minutes: 90,
  occasional_saturated_minutes: 180,
  consecutive_elevated: 3,
  consecutive_saturated: 5,
  active_sessions_elevated: 2,
  active_sessions_saturated: 3,
  pause_fatigue_threshold: 0,
  rolling_window_hours: 4,
  rolling_elevated_minutes: 120,
  rolling_saturated_minutes: 180,
};

export function DlgThresholdSettings() {
  const { activeShop } = useAuth();
  const { canManageDlgConfig } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dlgEnabled, setDlgEnabled] = useState(true);
  const [learningEnabled, setLearningEnabled] = useState(false);
  const [thresholds, setThresholds] = useState<ThresholdValues>(DEFAULTS);

  const shopId = activeShop?.id;

  useEffect(() => {
    if (!shopId) return;
    loadSettings();
  }, [shopId]);

  const loadSettings = async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      // Load feature flags
      const { data: profile } = await supabase
        .from('shop_profile')
        .select('dlg_enabled, pensieve_dlg_learning_enabled')
        .eq('shop_id', shopId)
        .maybeSingle();

      if (profile) {
        setDlgEnabled(profile.dlg_enabled ?? true);
        setLearningEnabled(profile.pensieve_dlg_learning_enabled ?? false);
      }

      // Load thresholds
      const { data: config } = await supabase
        .from('dlg_threshold_config')
        .select('*')
        .eq('shop_id', shopId)
        .maybeSingle();

      if (config) {
        setThresholds({
          focused_elevated_minutes: config.focused_elevated_minutes,
          focused_saturated_minutes: config.focused_saturated_minutes,
          balanced_elevated_minutes: config.balanced_elevated_minutes,
          balanced_saturated_minutes: config.balanced_saturated_minutes,
          occasional_elevated_minutes: config.occasional_elevated_minutes,
          occasional_saturated_minutes: config.occasional_saturated_minutes,
          consecutive_elevated: config.consecutive_elevated,
          consecutive_saturated: config.consecutive_saturated,
          active_sessions_elevated: config.active_sessions_elevated,
          active_sessions_saturated: config.active_sessions_saturated,
          pause_fatigue_threshold: config.pause_fatigue_threshold,
          rolling_window_hours: config.rolling_window_hours,
          rolling_elevated_minutes: config.rolling_elevated_minutes,
          rolling_saturated_minutes: config.rolling_saturated_minutes,
        });
      }
    } catch (err) {
      console.error('Failed to load DLG settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!shopId) return;
    setSaving(true);
    try {
      // Update feature flags
      const { error: profileErr } = await supabase
        .from('shop_profile')
        .update({
          dlg_enabled: dlgEnabled,
          pensieve_dlg_learning_enabled: learningEnabled,
        })
        .eq('shop_id', shopId);

      if (profileErr) throw profileErr;

      // Upsert thresholds
      const { error: threshErr } = await supabase
        .from('dlg_threshold_config')
        .upsert({
          shop_id: shopId,
          ...thresholds,
        }, { onConflict: 'shop_id' });

      if (threshErr) throw threshErr;

      toast.success('Diagnostic Load Guard settings saved');
    } catch (err) {
      console.error('Failed to save DLG settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateThreshold = (key: keyof ThresholdValues, value: number) => {
    setThresholds(prev => ({ ...prev, [key]: value }));
  };

  if (!canManageDlgConfig) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Diagnostic Load Guard
        </CardTitle>
        <CardDescription>
          Configure diagnostic fatigue detection thresholds. These settings determine when technicians receive load advisories.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Feature Flags */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable DLG Advisories</Label>
              <p className="text-xs text-muted-foreground">Show diagnostic load state on assignment</p>
            </div>
            <Switch checked={dlgEnabled} onCheckedChange={setDlgEnabled} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Adaptive Learning</Label>
              <p className="text-xs text-muted-foreground">Automatically adjust thresholds based on override patterns</p>
            </div>
            <Switch checked={learningEnabled} onCheckedChange={setLearningEnabled} />
          </div>
        </div>

        <Separator />

        {/* Minute Thresholds by Preference */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Daily Minute Thresholds</h4>
          <div className="grid grid-cols-3 gap-4">
            {(['focused', 'balanced', 'occasional'] as const).map(pref => (
              <div key={pref} className="space-y-2">
                <Label className="capitalize text-xs">{pref}</Label>
                <div className="space-y-1">
                  <div>
                    <span className="text-xs text-muted-foreground">Elevated</span>
                    <Input
                      type="number"
                      value={thresholds[`${pref}_elevated_minutes`]}
                      onChange={e => updateThreshold(`${pref}_elevated_minutes`, Number(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Saturated</span>
                    <Input
                      type="number"
                      value={thresholds[`${pref}_saturated_minutes`]}
                      onChange={e => updateThreshold(`${pref}_saturated_minutes`, Number(e.target.value))}
                      className="h-8"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Rolling Window */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Rolling Window</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Window (hours)</Label>
              <Input
                type="number"
                value={thresholds.rolling_window_hours}
                onChange={e => updateThreshold('rolling_window_hours', Number(e.target.value))}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Elevated (min)</Label>
              <Input
                type="number"
                value={thresholds.rolling_elevated_minutes}
                onChange={e => updateThreshold('rolling_elevated_minutes', Number(e.target.value))}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Saturated (min)</Label>
              <Input
                type="number"
                value={thresholds.rolling_saturated_minutes}
                onChange={e => updateThreshold('rolling_saturated_minutes', Number(e.target.value))}
                className="h-8"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Consecutive & Sessions */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Session Thresholds</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Consecutive Elevated</Label>
              <Input
                type="number"
                value={thresholds.consecutive_elevated}
                onChange={e => updateThreshold('consecutive_elevated', Number(e.target.value))}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Consecutive Saturated</Label>
              <Input
                type="number"
                value={thresholds.consecutive_saturated}
                onChange={e => updateThreshold('consecutive_saturated', Number(e.target.value))}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Active Sessions Elevated</Label>
              <Input
                type="number"
                value={thresholds.active_sessions_elevated}
                onChange={e => updateThreshold('active_sessions_elevated', Number(e.target.value))}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Active Sessions Saturated</Label>
              <Input
                type="number"
                value={thresholds.active_sessions_saturated}
                onChange={e => updateThreshold('active_sessions_saturated', Number(e.target.value))}
                className="h-8"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Fatigue */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Fatigue Detection</h4>
          <div className="max-w-[200px]">
            <Label className="text-xs">Pause Frequency Threshold</Label>
            <Input
              type="number"
              value={thresholds.pause_fatigue_threshold}
              onChange={e => updateThreshold('pause_fatigue_threshold', Number(e.target.value))}
              className="h-8"
            />
            <p className="text-xs text-muted-foreground mt-1">0 = disabled</p>
          </div>
        </div>

        <div className="flex justify-between items-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setThresholds(DEFAULTS)}
          >
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
